import * as yup from 'yup';
import { useEffect } from 'react';
import Stack from '@mui/material/Stack/Stack';
import { useTranslation } from 'react-i18next';
import { FormikProvider, useFormik } from 'formik';

import { useVenmoSubmit } from './../../hooks/use-venmo-submit';
import { InputField } from '../../../../../InputField/InputField';
import { useCheckout } from '../../../../../../hooks/use-checkout';
import { AddressElement } from '../../../AddressElement/AddressElement';
import { PayButton } from '../../../PaymentForm/components/PayButton/PayButton';
import { EMAIL_FIELD_DEFAULT_ATTRIBUTES } from '../../../../../../constants/consts';
import { useAddressElementRef } from '../../../AddressElement/hooks/use-address-element-ref';
import { useShowTaxCalculationFields } from '../../../../hooks/use-show-tax-calculation-fields';
import { PaymentElementContainer } from '../../../PaymentElementContainer/PaymentElementContainer';
import { apmLatencyTracker } from '../../../../../../utils/apm-latency-tracker';
import { getCheckoutEmailSchema } from '../../../../../../utils/checkout-email-validation';

export function Venmo() {
  const { checkoutData, selectedPaymentMethod } = useCheckout();
  const { handleSubmit } = useVenmoSubmit();
  const { t } = useTranslation(undefined, { keyPrefix: 'checkout' });
  const { useTaxCalculationField, showAddressField } = useShowTaxCalculationFields();
  const { AddressElementRef } = useAddressElementRef();

  useEffect(() => {
    try {
      apmLatencyTracker.startTracking('Venmo', selectedPaymentMethod?.id);
    } catch (error) {
      console.error('Failed to start APM latency tracking:', error);
    }

    const timer = setTimeout(() => {
      try {
        apmLatencyTracker.endTracking('Venmo', selectedPaymentMethod?.id, {
          paymentMethodName: selectedPaymentMethod?.paymentMethodName,
          paymentMethodDisplayName: selectedPaymentMethod?.paymentMethodDisplayName,
          provider: 'Braintree',
          type: 'APM',
        });
      } catch (error) {
        console.error('Failed to end APM latency tracking:', error);
      }
    }, 100);

    return () => clearTimeout(timer);
  }, [selectedPaymentMethod?.id]);

  const formik = useFormik<{ email: string }>({
    initialValues: {
      email: checkoutData?.customer.email || '',
    },
    validateOnMount: true,
    enableReinitialize: true,
    validationSchema: yup.object().shape({
      email: getCheckoutEmailSchema({
        requiredMessage: t('fieldErrors.incompleteEmail'),
        invalidMessage: t('fieldErrors.invalidEmail'),
      }),
    }),
    onSubmit: (values) => handleSubmit(values.email, AddressElementRef.current),
  });

  return (
    <PaymentElementContainer
      content={
        <Stack spacing={1.5}>
          <FormikProvider value={formik}>
            <InputField
              name="email"
              label={t('email')}
              inputProps={{
                placeholder: t('emailAddress'),
                ...EMAIL_FIELD_DEFAULT_ATTRIBUTES,
              }}
            />
          </FormikProvider>
          {useTaxCalculationField && (
            <AddressElement ref={AddressElementRef} zipOnly={!showAddressField} />
          )}
        </Stack>
      }
    >
      <PayButton onClick={formik.submitForm} />
    </PaymentElementContainer>
  );
}
